import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Loader2, Sparkles, ArrowLeft } from "lucide-react";

export default function PublicLook() {
  const { token } = useParams();
  const [look, setLook] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/looks/${token}`)
      .then(({ data }) => setLook(data))
      .catch(() => setError(true));
  }, [token]);

  if (error) return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-6">
      <div className="text-center">
        <div className="overline text-zinc-500">§ 404</div>
        <div className="font-serif text-6xl mt-2">Look not found.</div>
        <p className="text-zinc-400 mt-4">This share link is disabled or was never public.</p>
        <Link to="/" className="mt-8 inline-flex overline items-center gap-2 hover:text-lime-400" data-testid="public-look-home">
          <ArrowLeft className="w-3.5 h-3.5" /> InstaLux
        </Link>
      </div>
    </div>
  );
  if (!look) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-lime-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="public-look-view">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        <div className="lg:col-span-8 relative bg-zinc-900 flex items-center justify-center overflow-hidden">
          <img
            src={fileUrl(look.image_path)}
            alt={look.title}
            className="max-w-full max-h-screen object-contain reveal"
            data-testid="public-look-image"
          />
        </div>
        <aside className="lg:col-span-4 p-8 md:p-12 flex flex-col justify-between">
          <div>
            <Link to="/" className="overline flex items-center gap-2 text-zinc-500 hover:text-lime-400 transition-colors" data-testid="public-look-brand">
              <span className="w-1.5 h-1.5 bg-lime-400 rounded-full" />
              Insta<span className="italic font-serif">Lux</span>
            </Link>
            <div className="mt-16">
              <div className="overline text-zinc-500 mb-2">A look by {look.creator_name || "a designer"}</div>
              <h1 className="font-serif text-5xl md:text-6xl leading-[0.9]" data-testid="public-look-title">{look.title}</h1>
              <div className="mt-6 space-y-3 text-sm">
                {look.category && <Row k="Category" v={look.category} />}
                {look.fabric && <Row k="Fabric" v={look.fabric} />}
                {look.silhouette && <Row k="Silhouette" v={look.silhouette} />}
                {look.length && <Row k="Length" v={look.length} />}
                {look.color && <Row k="Palette" v={look.color} />}
              </div>
            </div>
          </div>
          <div className="mt-16">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-lime-400 text-zinc-950 overline hover:bg-zinc-50 transition-colors"
              data-testid="public-look-cta"
            >
              <Sparkles className="w-3.5 h-3.5" /> Design your own
            </Link>
            <p className="mt-4 text-xs text-zinc-500">Rendered in InstaLux — an AI design studio for clothing, footwear, and accessories.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-zinc-800 py-2">
      <span className="overline text-zinc-500">{k}</span>
      <span className="font-serif italic text-lg">{v}</span>
    </div>
  );
}
